// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import appColors, { alpha } from '@/theme/colors';
import type { TourRoundSummary, SidebarSelection } from '../types';
import { roundDisplayLabel } from '../utils';
import SidebarItem from './SidebarItem';

type Props = {
  round: TourRoundSummary;
  index: number;
  expanded: boolean;
  selection: SidebarSelection;
  onToggleExpand: () => void;
  onSelect: (selection: SidebarSelection) => void;
  onDelete: () => void;
  onGoToRound: () => void;
  /**
   * Desktop selects the round's settings when the header is tapped; the mobile
   * full-screen menu must not, or the tap would push a detail view instead of
   * just expanding the round.
   */
  selectOnHeaderPress?: boolean;
  showChevrons?: boolean;
};

export default function RoundSidebarSection({
  round,
  index,
  expanded,
  selection,
  onToggleExpand,
  onSelect,
  onDelete,
  onGoToRound,
  selectOnHeaderPress = true,
  showChevrons = false,
}: Props) {
  const { t } = useI18n();
  const roundLabel = roundDisplayLabel(round, index, t);
  const dateLabel = round.playDate || '';
  const deleteTooltip = t('tourEdit.sidebarDeleteRoundA11y', { name: roundLabel });

  const deleteRef = useCallback((node: View | null) => {
    if (node) (node as unknown as HTMLElement).title = deleteTooltip;
  }, [deleteTooltip]);

  const isRoundItemSelected = (kind: SidebarSelection['kind']) => {
    if (!('roundId' in selection)) return false;
    return selection.kind === kind && selection.roundId === round.id;
  };

  return (
    <View style={styles.container}>
      <Pressable style={styles.header} onPress={() => { onToggleExpand(); if (selectOnHeaderPress) onSelect({ kind: 'round-settings', roundId: round.id }); }}>
        <View style={styles.headerLeft}>
          <Text style={styles.expandIcon}>{expanded ? '▾' : '▸'}</Text>
          <View style={styles.headerText}>
            {dateLabel ? <Text style={styles.dateText}>{dateLabel}</Text> : null}
            <Text style={styles.roundName} numberOfLines={1}>{roundLabel}</Text>
          </View>
        </View>
        <Pressable
          ref={deleteRef}
          style={styles.deleteButton}
          onPress={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          accessibilityLabel={deleteTooltip}
        >
          <Text style={styles.deleteIcon}>×</Text>
        </Pressable>
      </Pressable>
      {expanded && (
        <View style={styles.subItems}>
          <SidebarItem
            label={t('tourEdit.sidebarRoundSettings')}
            active={isRoundItemSelected('round-settings')}
            indented
            chevron={showChevrons}
            onPress={() => onSelect({ kind: 'round-settings', roundId: round.id })}
          />
          <SidebarItem
            label={t('tourEdit.playersTitle')}
            active={isRoundItemSelected('round-players')}
            indented
            chevron={showChevrons}
            onPress={() => onSelect({ kind: 'round-players', roundId: round.id })}
          />
          <SidebarItem
            label={t('tourEdit.sidebarGames')}
            active={isRoundItemSelected('round-games')}
            indented
            chevron={showChevrons}
            onPress={() => onSelect({ kind: 'round-games', roundId: round.id })}
          />
          <SidebarItem
            label={t('tourEdit.sidebarGoToRound')}
            active={false}
            indented
            onPress={onGoToRound}
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 2,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  expandIcon: {
    fontSize: 12,
    color: appColors.text.muted,
    width: 14,
  },
  headerText: {
    flex: 1,
  },
  dateText: {
    fontSize: 11,
    color: appColors.text.muted,
    fontWeight: '500',
  },
  roundName: {
    fontSize: 14,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  deleteButton: {
    width: 26,
    height: 26,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(appColors.status.danger, 0.2),
    borderWidth: 1,
    borderColor: alpha(appColors.status.danger, 0.4),
  },
  deleteIcon: {
    fontSize: 15,
    fontWeight: '700',
    color: appColors.status.danger,
    lineHeight: 17,
  },
  subItems: {
    marginLeft: 4,
  },
});
